// Daily QA Score for the current window + dashed prior-period overlay
// on the same day-index axis for visual comparison.
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { EChart } from '@/components/charts/EChart'
import ScorecardGraphContainer from '@/views/Scorecards/DashboardPage/GraphContainer'

import { CHART } from '@/components/charts/chartPalette'
import { useDayClickFilter } from '../hooks/useDayClickFilter'

// Map a by_date `aggregates` row to `{ date, day, score }`. Per TREN-308,
// `date` (YYYY-MM-DD) is the safe key; `day` (MM/DD) is kept for the
// human-readable tooltip line.
const normalizeRow = (row) => ({
  date: row?.date || null,
  day: row?.day || null,
  score: parseFloat(row?.['QA Score']) || 0,
})

// Format a YYYY-MM-DD or MM/DD string for the tooltip — falls back to the
// raw value if we can't parse it.
const formatTooltipDate = (raw) => {
  if (!raw) {
    return ''
  }
  const rawString = String(raw)
  let parsed = null
  let match = rawString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) {
    parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  } else {
    match = rawString.match(/^(\d{1,2})\/(\d{1,2})$/)
    if (match) {
      parsed = new Date(new Date().getFullYear(), Number(match[1]) - 1, Number(match[2]))
    }
  }
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return rawString
  }
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export const ScoreTrendsWidget = () => {
  const { data, loading } = useSelector((state) => state.qaCopilot)
  const onDayClick = useDayClickFilter()

  const currentRows = useMemo(
    () => (data.reportingByDay?.aggregates || []).map(normalizeRow),
    [data.reportingByDay]
  )

  // Prior-window rollup comes from redux -- the dashboard bootstrap
  // action populates it alongside the current window so this widget
  // doesn't need a sibling round trip. Null when the bundle failed
  // its prior section; the ghost line just hides.
  const priorByDay = data.reportingByDayPrior

  const priorRows = useMemo(() => (priorByDay?.aggregates || []).map(normalizeRow), [priorByDay])

  const option = useMemo(() => {
    // Both series share the same day-index x-axis. The longer of the
    // two periods sets the axis length; the shorter series just stops
    // early. We keep the current period's days as the visible axis
    // labels so the click-to-drill behavior maps back to the right date.
    const xLen = Math.max(currentRows.length, priorRows.length)
    const xAxisData = Array.from({ length: xLen }, (_, index) => {
      const row = currentRows[index]
      return row?.day || String(index + 1)
    })

    const series = [
      {
        name: 'Current period',
        type: 'line',
        data: currentRows.map((row) => row.score),
        smooth: true,
        showSymbol: true,
        symbolSize: 6,
        lineStyle: { color: CHART.BRAND, width: 2 },
        itemStyle: { color: CHART.BRAND },
        areaStyle: { color: CHART.BRAND, opacity: 0.12 },
      },
    ]

    if (!isEmpty(priorRows)) {
      series.push({
        name: 'Prior period',
        type: 'line',
        data: priorRows.map((row) => row.score),
        smooth: true,
        showSymbol: false,
        // Disable the day-click filter on the prior series: filtering
        // the dashboard to a prior-window date would be confusing UX.
        silent: true,
        triggerEvent: false,
        lineStyle: { color: CHART.MUTED, width: 2, type: 'dashed' },
        itemStyle: { color: CHART.MUTED },
      })
    }

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          if (isEmpty(params)) {
            return ''
          }
          const idx = params[0].dataIndex
          const lines = [`Day ${idx + 1}`]
          const current = currentRows[idx]
          if (current) {
            lines.push(
              `Current (${formatTooltipDate(current.date || current.day)}): ${current.score.toFixed(0)}%`
            )
          }
          const prior = priorRows[idx]
          if (prior) {
            lines.push(
              `Prior&nbsp;&nbsp;&nbsp;(${formatTooltipDate(prior.date || prior.day)}): ${prior.score.toFixed(0)}%`
            )
          }
          return lines.join('<br/>')
        },
      },
      legend: {
        data: !isEmpty(priorRows) ? ['Current period', 'Prior period'] : ['Current period'],
        bottom: 0,
        textStyle: { fontSize: 12 },
      },
      // top leaves room for the yAxis name ("QA Score") which renders
      // ABOVE the grid -- with less it collides with the card's
      // "All Scorecards" subtitle.
      grid: { left: 48, right: 24, top: 40, bottom: 56 },
      xAxis: {
        type: 'category',
        data: xAxisData,
        boundaryGap: false,
        axisLabel: { fontSize: 10 },
        name: 'Day',
        nameLocation: 'middle',
        nameGap: 28,
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'QA Score',
        axisLabel: { formatter: '{value}%' },
      },
      series,
    }
  }, [currentRows, priorRows])

  // Click-to-drill is gated on the current-period series so clicks on
  // the prior (dashed) line don't fire a confusing filter narrow.
  const events = useMemo(
    () => ({
      click: (params) => {
        if (params?.seriesName !== 'Current period') {
          return
        }
        const idx = params?.dataIndex
        const row = currentRows[idx]
        if (!row) {
          return
        }
        onDayClick(row.date || row.day)
      },
    }),
    [currentRows, onDayClick]
  )

  return (
    <ScorecardGraphContainer
      loading={loading.reportingByDay}
      dataTestId="scores-by-day-chart"
      title="Score Trends"
      info="Average QA score over time for the current filters — the overall trend line for the selected window."
      subTitle="All Scorecards"
      noData={isEmpty(data.reportingByDay?.aggregates)}
      graph={
        <div style={{ height: '364px' }}>
          <EChart option={option} events={events} style={{ height: '100%', width: '100%' }} />
        </div>
      }
    />
  )
}
